import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  CircleDollarSign,
  Shield,
  FileCheck,
  TrendingUp,
  CheckCircle,
  XCircle,
  Scale,
  FileText,
  Landmark,
  Calendar,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const GestaoPositiva = () => {
  const { t } = useLanguage();

  const benefits = [
    { icon: <TrendingUp className="h-6 w-6" />, titleKey: 'gptPage.benefit1', descKey: 'gptPage.benefit1Desc' },
    { icon: <Shield className="h-6 w-6" />, titleKey: 'gptPage.benefit2', descKey: 'gptPage.benefit2Desc' },
    { icon: <CircleDollarSign className="h-6 w-6" />, titleKey: 'gptPage.benefit3', descKey: 'gptPage.benefit3Desc' },
    { icon: <Scale className="h-6 w-6" />, titleKey: 'gptPage.benefit4', descKey: 'gptPage.benefit4Desc' },
  ];

  const processSteps = [
    { step: 1, titleKey: 'gptPage.process1', descKey: 'gptPage.process1Desc' },
    { step: 2, titleKey: 'gptPage.process2', descKey: 'gptPage.process2Desc' },
    { step: 3, titleKey: 'gptPage.process3', descKey: 'gptPage.process3Desc' },
    { step: 4, titleKey: 'gptPage.process4', descKey: 'gptPage.process4Desc' },
    { step: 5, titleKey: 'gptPage.process5', descKey: 'gptPage.process5Desc' },
    { step: 6, titleKey: 'gptPage.process6', descKey: 'gptPage.process6Desc' },
  ];

  const whatItIsNot = [
    { textKey: 'gptPage.notTax', descKey: 'gptPage.notTaxDesc' },
    { textKey: 'gptPage.notBenefit', descKey: 'gptPage.notBenefitDesc' },
    { textKey: 'gptPage.notCompensation', descKey: 'gptPage.notCompensationDesc' },
    { textKey: 'gptPage.notCredit', descKey: 'gptPage.notCreditDesc' },
  ];

  const legalBases = [
    { 
      titleKey: 'gptPage.legal1',
      dateKey: 'gptPage.legal1Date',
      descKey: 'gptPage.legal1Desc'
    },
    { 
      titleKey: 'gptPage.legal2',
      dateKey: 'gptPage.legal2Date',
      descKey: 'gptPage.legal2Desc'
    },
    { 
      titleKey: 'gptPage.legal3',
      dateKey: 'gptPage.legal3Date',
      descKey: 'gptPage.legal3Desc'
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="gestao-positiva-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gptPage.heroTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('gptPage.heroTitle')}{' '}
                <span className="gold-sheen">{t('gptPage.heroHighlight')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                {t('gptPage.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="gpt-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('gptPage.ctaPrimary')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="bg-emerald p-8 rounded-sm text-white">
                <h3 className="text-xl font-serif font-bold mb-6">{t('gptPage.accountingTitle')}</h3>
                <div className="space-y-4">
                  <div className="bg-white/10 p-4 rounded-sm">
                    <p className="text-sm text-stone-300">{t('gptPage.asset')}</p>
                    <p className="font-semibold">{t('gptPage.assetDesc')}</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-sm">
                    <p className="text-sm text-stone-300">{t('gptPage.liability')}</p>
                    <p className="font-semibold">{t('gptPage.liabilityDesc')}</p>
                  </div>
                  <div className="bg-bronze/30 p-4 rounded-sm">
                    <p className="text-sm text-white/80">{t('gptPage.revenue')}</p>
                    <p className="font-semibold text-bronze">{t('gptPage.revenueDesc')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What It Is */}
      <section className="py-24 md:py-32 bg-white" data-testid="gpt-what-it-is">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gptPage.whatIsTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
                {t('gptPage.whatIsTitle')}
              </h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                {t('gptPage.whatIsDesc')}
              </p>
              <div className="bg-bronze/10 p-6 rounded-sm border-l-4 border-bronze">
                <p className="text-stone-700 font-medium">
                  {t('gptPage.whatIsHighlight')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-serif font-semibold text-stone-900 mb-6">
                {t('gptPage.whatIsNotTitle')}
              </h3>
              <div className="space-y-4">
                {whatItIsNot.map((item) => (
                  <div 
                    key={item.textKey}
                    className="flex items-start gap-4 bg-stone-50 p-4 rounded-sm"
                  >
                    <XCircle className="h-6 w-6 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-stone-900">{t(item.textKey)}</p>
                      <p className="text-stone-600 text-sm">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-emerald text-white" data-testid="gpt-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gptPage.benefitsTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">
              {t('gptPage.benefitsTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm"
                data-testid={`benefit-${index}`}
              >
                <div className="w-12 h-12 rounded-sm bg-bronze/20 flex items-center justify-center text-bronze mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold mb-2">
                  {t(benefit.titleKey)}
                </h3>
                <p className="text-stone-300 text-sm">
                  {t(benefit.descKey)}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 bg-white/10 px-6 py-4 rounded-sm">
              <Calendar className="h-6 w-6 text-bronze" />
              <p className="text-stone-200">
                {t('gptPage.creditUsage')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="gpt-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gptPage.processTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('gptPage.processTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-sm border border-stone-200"
                data-testid={`process-step-${index}`}
              >
                <div className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-emerald text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                    {step.step}
                  </span>
                  <div>
                    <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-stone-600 text-sm">
                      {t(step.descKey)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Basis */}
      <section className="py-24 md:py-32 bg-white" data-testid="gpt-legal">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gptPage.legalTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('gptPage.legalTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {legalBases.map((legal, index) => (
              <motion.div
                key={legal.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-stone-50 p-6 rounded-sm"
                data-testid={`legal-${index}`}
              >
                <Landmark className="h-8 w-8 text-emerald mb-4" />
                <h3 className="text-lg font-serif font-bold text-stone-900 mb-1">
                  {t(legal.titleKey)}
                </h3>
                <p className="text-bronze text-sm mb-3">{t(legal.dateKey)}</p>
                <p className="text-stone-600 text-sm">
                  {t(legal.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="py-24 md:py-32 bg-emerald text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gptPage.securityTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                {t('gptPage.securityTitle')}
              </h2>
              <p className="text-stone-300 mb-8 leading-relaxed">
                {t('gptPage.securityDesc')}
              </p>
              <ul className="space-y-4">
                {[
                  { icon: <CheckCircle className="h-5 w-5" />, textKey: 'gptPage.security1' },
                  { icon: <Shield className="h-5 w-5" />, textKey: 'gptPage.security2' },
                  { icon: <FileCheck className="h-5 w-5" />, textKey: 'gptPage.security3' },
                  { icon: <Lock className="h-5 w-5" />, textKey: 'gptPage.security4' },
                ].map((item) => (
                  <li key={item.textKey} className="flex items-center gap-3">
                    <span className="text-bronze">{item.icon}</span>
                    <span className="text-stone-200">{t(item.textKey)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
                <CircleDollarSign className="h-16 w-16 text-bronze mx-auto mb-4" />
                <h3 className="text-2xl font-serif font-bold mb-4">
                  {t('gptPage.studyTitle')}
                </h3>
                <p className="text-stone-300 mb-6">
                  {t('gptPage.studyDesc')}
                </p>
                <Button 
                  asChild
                  className="bg-bronze hover:bg-bronze-light text-white rounded-sm px-8 py-4"
                  data-testid="gpt-study-btn"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('gptPage.studyBtn')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('gptPage.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('gptPage.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="gpt-final-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('gptPage.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GestaoPositiva;
